import React, {
  ChangeEvent,
  FocusEvent,
  ForwardedRef,
  forwardRef,
  useRef,
  useState,
  useEffect,
} from 'react';
import { Placement } from '@floating-ui/react';
import { Props as InputProps } from '../../Input/Input';
import { ComboBoxListItem } from '../components/ComboBoxListItem';
import { ComboBoxList } from '../components/ComboBoxList';
import { useComboBox } from '../components/useComboBox';
import styles from './MultiSelectComboBox.module.scss';
import {
  filterOptions,
  filterUsedOptions,
  findCaseInsensitiveMatch,
  isValueFromList,
} from './utils';
import { Button, Icon, IconList, Input, VisuallyHidden } from 'components/core';
import { Inline, Stack } from 'components/layout';

export type MultiSelectComboBoxProps = {
  data?: string[];
  value: string[];
  listHeading?: string;
  placement?: Placement;
  noResultsMessage?: string;
  onChange: (value: string[]) => void;
  hasFixedOptions?: boolean;
  hasChevron?: boolean;
  enableFiltering?: boolean;
} & Omit<InputProps, 'onChange'>;

const MultiSelectComboBox = (
  {
    data = [],
    value = [],
    placeholder,
    placement,
    readOnly,
    listHeading,
    noResultsMessage = 'No matching results',
    hasFixedOptions = false,
    hasChevron = true,
    enableFiltering = false,
    onFocus,
    onChange,
    onBlur,
    ...rest
  }: MultiSelectComboBoxProps,
  passedInRef: ForwardedRef<HTMLInputElement>
) => {
  const [selectedValues, setSelectedValues] = useState(value);
  const [listOptions, setListOptions] = useState(
    filterUsedOptions(data, value)
  );
  const [isListOpen, setIsListOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeListItemIndex, setActiveListItemIndex] = useState<number | null>(
    null
  );
  const listRef = useRef<(HTMLElement | null)[]>([]);

  const {
    nodeId,
    context,
    refs,
    floatingStyles,
    mergedReferenceRefs,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
  } = useComboBox({
    isOpen: isListOpen,
    setIsOpen: setIsListOpen,
    placement,
    listRef,
    activeIndex: activeListItemIndex,
    setActiveIndex: setActiveListItemIndex,
    forwardedRef: passedInRef,
  });

  useEffect(() => {
    return () => setIsListOpen(false);
  }, []);

  useEffect(() => {
    // If selected values prop changes (e.g. from a form reset), then update component state
    setSelectedValues(value);
  }, [value]);

  const clearInput = () => setInputValue('');

  const moveFocusToInput = () => {
    const inputElement = refs.domReference.current;

    if (inputElement instanceof HTMLInputElement) {
      inputElement.focus();
    }
  };

  const handleSelection = (updatedValues: string[]) => {
    setSelectedValues(updatedValues);
    setListOptions(filterUsedOptions(data, updatedValues));
    onChange(updatedValues);
  };

  const addValuesToSelection = (value: string) => {
    const isValueInSelection = isValueFromList(selectedValues, value);
    const updatedSelectedValues = [...selectedValues, value];

    // Don't add duplicate value
    if (isValueInSelection) {
      clearInput();
      return;
    }

    clearInput();
    handleSelection(updatedSelectedValues);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (!isListOpen) {
      const isInList = isValueFromList(listOptions, inputValue);

      if (hasFixedOptions && isInList) {
        const matchedValue = findCaseInsensitiveMatch(listOptions, inputValue);
        addValuesToSelection(matchedValue || inputValue);
      }

      if (hasFixedOptions && !isInList) {
        clearInput();
      }

      // Add entered value to suggestion if it's not empty
      if (!hasFixedOptions && inputValue.trim()) {
        addValuesToSelection(inputValue);
      }
    }

    if (onBlur) {
      onBlur(event);
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (!readOnly && !isListOpen) {
      setIsListOpen(true);
    }

    if (onFocus) {
      onFocus(event);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<Element>) => {
    const isEnter = event.key === 'Enter';
    if (isEnter) event.preventDefault();

    // Autocomplete text for user when they hit enter
    if (
      isEnter &&
      activeListItemIndex !== null &&
      listOptions[activeListItemIndex]
    ) {
      setActiveListItemIndex(null);
      addValuesToSelection(listOptions[activeListItemIndex]);
    }

    // Add custom text to selection
    if (
      !hasFixedOptions &&
      isEnter &&
      activeListItemIndex === -1 &&
      Boolean(inputValue.trim())
    ) {
      addValuesToSelection(inputValue);
    }
  };

  const handleChange = (value: string) => {
    setInputValue(value);

    if (enableFiltering) {
      const updatedList = filterUsedOptions(data, selectedValues);
      setListOptions(filterOptions(updatedList, value));
    }

    if (value) {
      setIsListOpen(true);
      const index = listOptions.findIndex((item) =>
        item.toLowerCase().includes(value.toLowerCase())
      );
      setActiveListItemIndex(index);
    } else {
      setActiveListItemIndex(0);
      setIsListOpen(false);
    }
  };

  const handleRemove = (removedValue: string) => {
    // Add option back into menu list
    const updatedSelectedValues = selectedValues.filter(
      (value) => value !== removedValue
    );

    handleSelection(updatedSelectedValues);

    moveFocusToInput();
  };

  const shouldShowNoResultsMessage =
    enableFiltering && inputValue.trim() !== '' && listOptions.length === 0;

  return (
    <>
      <Stack spacing={2}>
        <Inline
          aria-live="polite"
          alignX="left"
          spacing={1}
          className={styles.selectionList}
        >
          {selectedValues.map((selectedValue) => {
            return (
              <Button
                className={styles.selectedItem}
                disabled={readOnly}
                type="neutral"
                variant="solid"
                size="xs"
                key={selectedValue}
                htmlType="button"
                aria-label={`Remove "${selectedValue}" from selected items`}
                onClick={() => handleRemove(selectedValue)}
              >
                {selectedValue}
                <Icon icon={IconList.light.faXmark} />
              </Button>
            );
          })}
        </Inline>

        <div className={styles.inputWrapper}>
          <Input
            {...rest}
            ref={mergedReferenceRefs}
            {...getReferenceProps({
              // Prevent Chrome from adding it's own autocomplete popup
              autoComplete: 'off',
              readOnly,
              placeholder,
              value: inputValue,
              onFocus: handleFocus,
              onBlur: handleBlur,
              onChange: (e: ChangeEvent<HTMLInputElement>) =>
                handleChange(e.currentTarget.value),
              onKeyDown: handleKeyDown,
            })}
          />
          {hasChevron && (
            <Icon
              className={styles.inputChevron}
              icon={IconList.solid.faAngleDown}
            />
          )}
          <VisuallyHidden component="div" role="status">
            {shouldShowNoResultsMessage ? noResultsMessage : ''}
          </VisuallyHidden>
        </div>
      </Stack>

      <ComboBoxList
        nodeId={nodeId}
        heading={listHeading}
        hasListItems={Boolean(listOptions.length)}
        hasPopupContent={
          Boolean(listOptions.length) || shouldShowNoResultsMessage
        }
        context={context}
        floatingRef={refs.setFloating}
        floatingStyles={floatingStyles}
        floatingProps={getFloatingProps()}
      >
        <Stack>
          {shouldShowNoResultsMessage && (
            <div className={styles.emptyStateMessage} aria-hidden="true">
              {noResultsMessage}
            </div>
          )}
          {listOptions.map((item, index) => (
            <ComboBoxListItem
              key={item}
              {...getItemProps({
                ref(node) {
                  listRef.current[index] = node;
                },
                onClick() {
                  addValuesToSelection(item);
                  moveFocusToInput();
                },
              })}
              isActive={activeListItemIndex === index}
            >
              {item}
            </ComboBoxListItem>
          ))}
        </Stack>
      </ComboBoxList>
    </>
  );
};

const forwardedMultiSelectComboBox = forwardRef<
  HTMLInputElement,
  MultiSelectComboBoxProps
>(MultiSelectComboBox);
export { forwardedMultiSelectComboBox as MultiSelectComboBox };
